import type { Contract } from '@gmn-portal/backend';
import { Eye, MoreHorizontal, Plus } from 'lucide-react';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useContracts, useDeleteContract } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from '@/shared/components/ui';

export function ContractSubcontractsTab({
  contractId,
  basePath,
  isGmnAdmin,
}: {
  contractId: string;
  basePath: string;
  isGmnAdmin: boolean;
}) {
  const { t } = useIntl();
  const navigate = useNavigate();
  const [contractToDelete, setContractToDelete] = useState<Contract | null>(null);
  const deleteContract = useDeleteContract();

  const { data: subcontractsData, isLoading } = useContracts({
    where: {
      parentContract: {
        equals: contractId,
      },
    },
    sort: 'startDate',
    limit: 100,
  });

  const subcontracts = useMemo(() => subcontractsData?.docs ?? [], [subcontractsData?.docs]);

  const handleDeleteContract = async () => {
    if (!contractToDelete) return;
    try {
      await deleteContract.mutateAsync(contractToDelete.id);
      toast.success(t('contracts.subcontractDeletedSuccessfully'));
      setContractToDelete(null);
    } catch (_error) {
      toast.error(t('contracts.errorDeletingSubcontract'));
    }
  };

  const ActionCell = ({ subcontract }: { subcontract: Contract }) => {
    return (
      // biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: Container only stops propagation, not interactive
      <div
        className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => navigate(`${basePath}/${subcontract.id}/general`)}
          className="p-1.5 hover:bg-secondary text-primary rounded-md transition-colors"
          title={t('common.view')}
        >
          <Eye size={16} />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="p-1.5 hover:bg-muted text-muted-foreground rounded-md transition-colors"
              title={t('common.actions')}
            >
              <MoreHorizontal size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => navigate(`${basePath}/${subcontract.id}`)}>
              {t('common.edit')}
            </DropdownMenuItem>
            {isGmnAdmin && (
              <DropdownMenuItem onClick={() => setContractToDelete(subcontract)}>
                {t('common.delete')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  return (
    <div className="grid gap-4" data-testid="contract-subcontracts-tab">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">{t('contracts.subcontracts')}</h2>
          <p className="text-sm text-muted-foreground">{t('contracts.subcontractsDescription')}</p>
        </div>
        <Button
          type="button"
          onClick={() => navigate(`${basePath}/new?parentId=${contractId}`)}
          disabled={!isGmnAdmin}
          data-testid="create-subcontract"
        >
          <Plus size={16} className="mr-2" />
          {t('contracts.createSubcontract')}
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {isLoading ? (
          <div className="p-4">
            <Skeleton className="h-8 w-full" />
            <div className="mt-3 grid gap-2">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
            </div>
          </div>
        ) : subcontracts.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            {t('contracts.noSubcontracts')}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableHeaderRow>
                <TableHead className="pl-6">{t('contracts.title')}</TableHead>
                <TableHead>{t('contracts.orderNumber')}</TableHead>
                <TableHead>{t('contracts.startDate')}</TableHead>
                <TableHead>{t('contracts.endDate')}</TableHead>
                <TableHead>{t('contracts.statusLabel')}</TableHead>
                <TableHead className="pr-6 text-right">
                  <span className="sr-only">{t('common.actions')}</span>
                </TableHead>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {subcontracts.map((subcontract) => {
                return (
                  <TableRow
                    key={subcontract.id}
                    data-testid={`subcontract-row-${subcontract.id}`}
                    className="cursor-pointer"
                    onClick={() => navigate(`${basePath}/${subcontract.id}/general`)}
                  >
                    <TableCell className="pl-6 font-medium">{subcontract.title}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {subcontract.orderNumber || '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {subcontract.startDate
                        ? DateTime.fromISO(subcontract.startDate).toFormat('dd.MM.yyyy')
                        : '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {subcontract.endDate
                        ? DateTime.fromISO(subcontract.endDate).toFormat('dd.MM.yyyy')
                        : '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {subcontract.status === 'closed'
                        ? t('contracts.status.closed')
                        : t('contracts.status.open')}
                    </TableCell>
                    <TableCell className="pr-6 text-right">
                      <ActionCell subcontract={subcontract} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <AlertDialog
        open={!!contractToDelete}
        onOpenChange={(open) => !open && setContractToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contracts.deleteSubcontract')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('contracts.deleteSubcontractConfirmation', {
                name: contractToDelete?.orderNumber || contractToDelete?.title || '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteContract}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
